import type { Case } from '../types/case';

// The Mediator Service sits in front of the Resolution Engine and handles:
//   - Mapping a Zoom meeting id → Session → Case
//   - Generating per-attendee Zoom invite links (uses meeting:write:invite_links)
//
// Both run as Cloudflare Pages Functions under /api/mediator/* on the same
// origin as the React app. The mediator's Zoom OAuth access token is passed
// via Authorization so the function can act on their behalf.

export interface InviteRequest {
  sessionId: string;
  partyId: string;
  partyName: string;
  meetingId: string;
}

export interface InviteResponse {
  party_id: string;
  party_name: string;
  join_url: string;
  expires_in: number;
}

export interface SessionLookupResponse {
  session_id: string;
  session: Session;
  case: Case;
}

export class MediatorServiceError extends Error {
  readonly status: number;
  /** Engine error code (e.g. REVIEW_INCOMPLETE, STALE_EDIT) when present. */
  readonly code?: string;
  /** Why the background agreement draft failed (engine surfaces it on the
      404 while no draft exists) — shown instead of an endless spinner. */
  readonly draftError?: string;
  constructor(status: number, message: string, code?: string, draftError?: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.draftError = draftError;
  }
}

// 401 recovery (Rachel, 2026-10-08): the sidebar authenticates with the
// mediator's Zoom OAuth access token, which Zoom expires after ~1 hour. When
// it lapsed mid-session, every call 401'd and the panel bricked with no
// re-auth. useAuth registers a refresher here: on a 401 we refresh the Zoom
// token ONCE and retry the call transparently; only a dead refresh token
// (refresher returns null) surfaces the 401 so the UI can prompt sign-in.
type TokenRefresher = () => Promise<string | null>;
let tokenRefresher: TokenRefresher | null = null;
export function setMediatorTokenRefresher(fn: TokenRefresher | null): void {
  tokenRefresher = fn;
}

async function request<T>(
  path: string,
  init: RequestInit,
  accessToken: string,
  retried = false,
): Promise<T> {
  const res = await fetch(`/api/mediator${path}`, {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      authorization: `Bearer ${accessToken}`,
      'content-type': 'application/json',
    },
  });

  // Expired Zoom token → refresh once and retry with the fresh token.
  if (res.status === 401 && !retried && tokenRefresher) {
    const fresh = await tokenRefresher().catch(() => null);
    if (fresh) return request<T>(path, init, fresh, true);
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: string | { code?: string; message?: string; draft_error?: string };
    };
    const message =
      typeof body.error === 'string'
        ? body.error
        : typeof body.error === 'object' && body.error?.message
          ? body.error.message
          : `Mediator service error (${res.status})`;
    const code = typeof body.error === 'object' ? body.error?.code : undefined;
    const draftError = typeof body.error === 'object' ? body.error?.draft_error : undefined;
    throw new MediatorServiceError(res.status, message, code, draftError);
  }
  return (await res.json()) as T;
}

export async function lookupSession(
  meetingId: string,
  accessToken: string,
): Promise<SessionLookupResponse | null> {
  try {
    return await request<SessionLookupResponse>(
      `/sessions?zoom_meeting_id=${encodeURIComponent(meetingId)}`,
      { method: 'GET' },
      accessToken,
    );
  } catch (err) {
    if (err instanceof MediatorServiceError && err.status === 404) {
      // A 404 carrying draft_error is an ANSWER (the background draft
      // failed) — propagate it so the poll can stop and say why.
      if (err.draftError) throw err;
      return null;
    }
    throw err;
  }
}

export async function inviteParty(
  req: InviteRequest,
  accessToken: string,
): Promise<InviteResponse> {
  return request<InviteResponse>(
    `/sessions/${encodeURIComponent(req.sessionId)}/invite`,
    {
      method: 'POST',
      body: JSON.stringify({
        party_id: req.partyId,
        party_name: req.partyName,
        meeting_id: req.meetingId,
      }),
    },
    accessToken,
  );
}

// ============================================================
// Phase 2 — session lifecycle
// ============================================================

export type SessionStatus = 'scheduled' | 'consenting' | 'in_progress' | 'ended';

export interface PhoneJoin {
  number: string;
  passcode: string | null;
  meeting_id: string;
}

export interface Session {
  id: string;
  case_id: string;
  zoom_meeting_id: string;
  zoom_meeting_uuid: string | null;
  mediator_zoom_user_id: string | null;
  status: SessionStatus;
  consent_given_at: string | null;
  started_at: string | null;
  ended_at: string | null;
  rtms_stream_id: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  /** Parties who have NOT signed the Agreement to Mediate (A2M no longer
      gates scheduling — the sidebar flags these live, 2026-10-30). */
  a2m_missing?: Array<{ party_id: string; name: string }>;
}

export interface TranscriptSegment {
  id: string;
  session_id: string;
  speaker_label: string;
  party_id: string | null;
  content: string;
  start_time: number;
  end_time: number;
  confidence: number | null;
  is_corrected: boolean;
  created_at: string;
}

export async function createSession(
  input: { case_id: string; zoom_meeting_id: string; mediator_zoom_user_id?: string },
  accessToken: string,
): Promise<Session> {
  return request<Session>(
    `/sessions`,
    { method: 'POST', body: JSON.stringify({ ...input, metadata: {} }) },
    accessToken,
  );
}

export async function startSession(
  sessionId: string,
  accessToken: string,
): Promise<Session> {
  return request<Session>(
    `/sessions/${encodeURIComponent(sessionId)}/start`,
    { method: 'POST', body: '{}' },
    accessToken,
  );
}

export async function endSession(
  sessionId: string,
  accessToken: string,
): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(
    `/sessions/${encodeURIComponent(sessionId)}/end`,
    { method: 'POST', body: '{}' },
    accessToken,
  );
}

/** Zoom dials the party's phone and joins them into the live meeting.
    Requires the Premium Audio add-on account-side; the engine surfaces
    Zoom's error verbatim when it isn't enabled. */
export async function dialParty(
  sessionId: string,
  partyId: string,
  accessToken: string,
): Promise<{ ok: boolean; calling: string }> {
  return request<{ ok: boolean; calling: string }>(
    `/sessions/${encodeURIComponent(sessionId)}/dial`,
    { method: 'POST', body: JSON.stringify({ party_id: partyId }) },
    accessToken,
  );
}

/** Rename a transcript speaker (e.g. "Call-in user" → the party's name).
    Rewrites past lines and applies to future ones. */
export async function relabelSpeaker(
  sessionId: string,
  fromLabel: string,
  toLabel: string,
  accessToken: string,
): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(
    `/sessions/${encodeURIComponent(sessionId)}/relabel-speaker`,
    { method: 'POST', body: JSON.stringify({ from_label: fromLabel, to_label: toLabel }) },
    accessToken,
  );
}

/** Mediation attempted, no agreement: ends the session AND produces the
    "not resolved" ROM draft for review. No stipulation is created. */
export async function sendA2mPush(
  sessionId: string,
  partyId: string,
  accessToken: string,
): Promise<{ ok: boolean; queued?: number }> {
  return request<{ ok: boolean; queued?: number }>(
    `/sessions/${encodeURIComponent(sessionId)}/a2m-push`,
    { method: 'POST', body: JSON.stringify({ party_id: partyId }) },
    accessToken,
  );
}

/** Mediation attempted, no agreement: ends the session AND produces the
    "not resolved" ROM draft for review. No stipulation is created. */
export async function endSessionUnresolved(
  sessionId: string,
  accessToken: string,
  reason?: string,
): Promise<{ ok: boolean; rom_agreement_id: string | null }> {
  return request<{ ok: boolean; rom_agreement_id: string | null }>(
    `/sessions/${encodeURIComponent(sessionId)}/end-unresolved`,
    { method: 'POST', body: JSON.stringify(reason ? { reason } : {}) },
    accessToken,
  );
}

// ============================================================
// Phase 3 — copilot suggestions + key moments
// ============================================================

export type SuggestionType =
  | 'fact_check'
  | 'policy'
  | 'math'
  | 'strategy'
  | 'key_moment'
  | 'agreement_term';
export type SuggestionPriority = 'high' | 'medium' | 'low';
export type SuggestionStatus = 'active' | 'dismissed' | 'pinned';

export interface CopilotSuggestion {
  id: string;
  session_id: string;
  type: SuggestionType;
  priority: SuggestionPriority;
  content: string;
  reference: string | null;
  status: SuggestionStatus;
  generated_at: string;
  dismissed_at: string | null;
  pinned_at: string | null;
}

export type MomentType = 'general' | 'agreement_term' | 'factual' | 'concession';
export type MomentSource = 'ai' | 'mediator';

export interface KeyMoment {
  id: string;
  session_id: string;
  transcript_segment_id: string | null;
  source: MomentSource;
  summary: string;
  moment_type: MomentType;
  timestamp_in_session: number | null;
  created_at: string;
}

export async function dismissSuggestion(
  sessionId: string,
  suggestionId: string,
  accessToken: string,
): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(
    `/sessions/${encodeURIComponent(sessionId)}/suggestions/${encodeURIComponent(
      suggestionId,
    )}/dismiss`,
    { method: 'POST', body: '{}' },
    accessToken,
  );
}

export async function pinSuggestion(
  sessionId: string,
  suggestionId: string,
  accessToken: string,
): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(
    `/sessions/${encodeURIComponent(sessionId)}/suggestions/${encodeURIComponent(
      suggestionId,
    )}/pin`,
    { method: 'POST', body: '{}' },
    accessToken,
  );
}

export async function listSuggestions(
  sessionId: string,
  accessToken: string,
): Promise<CopilotSuggestion[]> {
  const res = await request<{ suggestions: CopilotSuggestion[] }>(
    `/sessions/${encodeURIComponent(sessionId)}/suggestions`,
    { method: 'GET' },
    accessToken,
  );
  return res.suggestions;
}

export async function listMoments(
  sessionId: string,
  accessToken: string,
): Promise<KeyMoment[]> {
  const res = await request<{ moments: KeyMoment[] }>(
    `/sessions/${encodeURIComponent(sessionId)}/moments`,
    { method: 'GET' },
    accessToken,
  );
  return res.moments;
}

export async function createMoment(
  sessionId: string,
  summary: string,
  accessToken: string,
  opts?: { moment_type?: MomentType; transcript_segment_id?: string },
): Promise<KeyMoment> {
  return request<KeyMoment>(
    `/sessions/${encodeURIComponent(sessionId)}/moments`,
    {
      method: 'POST',
      body: JSON.stringify({
        summary,
        moment_type: opts?.moment_type ?? 'general',
        transcript_segment_id: opts?.transcript_segment_id,
      }),
    },
    accessToken,
  );
}

export async function deleteMoment(
  sessionId: string,
  momentId: string,
  accessToken: string,
): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(
    `/sessions/${encodeURIComponent(sessionId)}/moments/${encodeURIComponent(momentId)}`,
    { method: 'DELETE' },
    accessToken,
  );
}

// ============================================================
// Phase 4 — Agreements
// ============================================================

export type AgreementStatus = 'draft' | 'review' | 'sent' | 'signed' | 'voided';

export interface AgreementParty {
  name: string;
  role: string;
}

export interface AgreementSection {
  section: string;
  terms: string[];
}

export interface AgreementContent {
  title: string;
  date: string;
  parties: AgreementParty[];
  /** Dropped from drafting 2026-08-05 (Danny: terms only) — optional for
      agreements drafted before then. */
  background?: string;
  agreed_terms: AgreementSection[];
  signatures_required: AgreementParty[];
  mediator: string;
  confidentiality_notice: string;
}

export interface Agreement {
  id: string;
  session_id: string;
  case_id: string;
  content_json: AgreementContent;
  status: AgreementStatus;
  created_at: string;
  updated_at: string;
}

/** Kick off drafting. Opus drafting runs server-side in the background
    (its latency must never race the HTTP response — Mo hit "internal
    server error" while the draft quietly committed, 2026-08-28). A 202
    { status: 'generating' } means poll getAgreement until a draft newer
    than the press appears; a direct Agreement is a recent draft being
    reused (retry after a lost response). */
export async function draftAgreement(
  sessionId: string,
  accessToken: string,
): Promise<Agreement | { status: 'generating' }> {
  return request<Agreement | { status: 'generating' }>(
    `/sessions/${encodeURIComponent(sessionId)}/draft-agreement`,
    { method: 'POST', body: '{}' },
    accessToken,
  );
}

/** Save mediator edits to the latest DRAFT agreement (full content replace;
    the engine re-applies signature hygiene and audits the edit). */
export async function updateAgreement(
  sessionId: string,
  content: AgreementContent,
  accessToken: string,
): Promise<Agreement> {
  return request<Agreement>(
    `/sessions/${encodeURIComponent(sessionId)}/agreement`,
    { method: 'PATCH', body: JSON.stringify({ content_json: content }) },
    accessToken,
  );
}

export interface SessionSigningStatus {
  agreement_id: string;
  total: number;
  signed: number;
  declined: number;
  pending: number;
  all_signed: boolean;
  signers: Array<{
    id: string;
    party_id: string | null;
    signer_name: string;
    role_label: string | null;
    status: 'pending' | 'signed' | 'declined' | 'voided';
    signed_at: string | null;
    declined_at: string | null;
    sign_url?: string;
  }>;
}

/** Text + email every signer their private signing link. Throws
    MediatorServiceError with code REVIEW_INCOMPLETE when the draft was
    shared for review and not everyone confirmed; retry with
    confirmOverride to proceed anyway (audited server-side). */
export async function requestSignatures(
  sessionId: string,
  accessToken: string,
  opts: { confirmOverride?: boolean } = {},
): Promise<{ signing: SessionSigningStatus }> {
  return request<{ signing: SessionSigningStatus }>(
    `/sessions/${encodeURIComponent(sessionId)}/agreement/request-signatures`,
    {
      method: 'POST',
      // ai_reviewed: the UI gates the button behind the AI-review checkbox,
      // so reaching this call IS the acknowledgment (server enforces too).
      body: JSON.stringify({ ai_reviewed: true, ...(opts.confirmOverride ? { confirm_override: true } : {}) }),
    },
    accessToken,
  );
}

// ============================================================
// Pre-signature party review
// ============================================================

export type ReviewEffectiveStatus = 'pending' | 'confirmed' | 'confirmed_stale' | 'changes_requested';

export interface SessionReviewStatus {
  agreement_id: string | null;
  revision: number | null;
  total: number;
  confirmed: number;
  stale: number;
  changes_requested: number;
  pending: number;
  all_confirmed: boolean;
  reviews: Array<{
    id: string;
    party_id: string;
    party_name: string | null;
    status: 'pending' | 'confirmed' | 'changes_requested';
    effective_status: ReviewEffectiveStatus;
    confirmed_revision: number | null;
    comment: string | null;
    updated_at: string;
    review_url?: string;
  }>;
}

/** Text + email the selected parties their private live review link. */
export async function shareReview(
  sessionId: string,
  partyIds: string[],
  accessToken: string,
): Promise<{ agreement_status: AgreementStatus; review: SessionReviewStatus }> {
  return request<{ agreement_status: AgreementStatus; review: SessionReviewStatus }>(
    `/sessions/${encodeURIComponent(sessionId)}/agreement/share-review`,
    { method: 'POST', body: JSON.stringify({ party_ids: partyIds }) },
    accessToken,
  );
}

export async function getReviewStatus(
  sessionId: string,
  accessToken: string,
): Promise<{ agreement_status: AgreementStatus; revision: number; review: SessionReviewStatus } | null> {
  try {
    return await request<{ agreement_status: AgreementStatus; revision: number; review: SessionReviewStatus }>(
      `/sessions/${encodeURIComponent(sessionId)}/agreement/review-status`,
      { method: 'GET' },
      accessToken,
    );
  } catch (err) {
    if (err instanceof MediatorServiceError && err.status === 404) return null;
    throw err;
  }
}

/** Render unsaved content exactly as the party pages will show it.
    Returns a standalone HTML page for an iframe. */
export async function previewAgreement(
  sessionId: string,
  content: AgreementContent,
  accessToken: string,
): Promise<string> {
  const { html } = await request<{ html: string }>(
    `/sessions/${encodeURIComponent(sessionId)}/agreement/preview`,
    { method: 'POST', body: JSON.stringify({ content_json: content }) },
    accessToken,
  );
  return html;
}

export async function getSessionSigningStatus(
  sessionId: string,
  accessToken: string,
): Promise<{ agreement_status: AgreementStatus; signing: SessionSigningStatus; settlement?: { amount: number | null; source: string | null } } | null> {
  try {
    return await request<{ agreement_status: AgreementStatus; signing: SessionSigningStatus; settlement?: { amount: number | null; source: string | null } }>(
      `/sessions/${encodeURIComponent(sessionId)}/agreement/signing-status`,
      { method: 'GET' },
      accessToken,
    );
  } catch (err) {
    if (err instanceof MediatorServiceError && err.status === 404) return null;
    throw err;
  }
}

export async function getAgreement(
  sessionId: string,
  accessToken: string,
): Promise<Agreement | null> {
  try {
    return await request<Agreement>(
      `/sessions/${encodeURIComponent(sessionId)}/agreement`,
      { method: 'GET' },
      accessToken,
    );
  } catch (err) {
    if (err instanceof MediatorServiceError && err.status === 404) return null;
    throw err;
  }
}

// ============================================================
// Dev / demo endpoints — stream scripted transcripts + reset state
// ============================================================

export async function pushDevTranscriptSegment(
  sessionId: string,
  segment: {
    speaker_label: string;
    content: string;
    start_time: number;
    end_time: number;
    confidence?: number;
  },
  accessToken: string,
): Promise<void> {
  await request<unknown>(
    `/sessions/${encodeURIComponent(sessionId)}/dev/push-transcript`,
    { method: 'POST', body: JSON.stringify(segment) },
    accessToken,
  );
}

export async function resetSessionState(
  sessionId: string,
  accessToken: string,
): Promise<void> {
  await request<unknown>(
    `/sessions/${encodeURIComponent(sessionId)}/dev/reset`,
    { method: 'POST', body: '{}' },
    accessToken,
  );
}

// ============================================================
// Resolve case — close the loop after agreement is drafted
// ============================================================

export interface ResolveCaseResponse {
  resolution_id: string;
  case_status: string;
  /** Close-out also produces the Report of Mediation (unless one exists). */
  rom_generated?: boolean;
}

export async function resolveCase(
  sessionId: string,
  accessToken: string,
  opts: { settlementAmount?: number | null } = {},
): Promise<ResolveCaseResponse> {
  return request<ResolveCaseResponse>(
    `/sessions/${encodeURIComponent(sessionId)}/resolve`,
    { method: 'POST', body: JSON.stringify(typeof opts.settlementAmount === 'number' ? { settlement_amount: opts.settlementAmount } : {}) },
    accessToken,
  );
}

/**
 * Opens a WebSocket to the session's transcript stream. The Cloudflare Pages
 * Function at `/api/mediator/[[path]].ts` proxies the upgrade to the
 * ResolutionEngine Durable Object while injecting the RE API key server-side.
 *
 * We rely on same-origin + UUID session ids for Phase 2.0 authorization —
 * browsers can't set the `Authorization` header on the upgrade request, and
 * echoing a bespoke subprotocol through the DO complicates handshake. The
 * endpoint only streams transcripts for a known session id, and session ids
 * are random UUIDs that only the authenticated sidebar ever learns.
 */
export async function openTranscriptStream(sessionId: string, accessToken: string): Promise<WebSocket> {
  // Phase-0 hardening (2026-09-24): the upgrade carries a short-lived
  // signed ticket minted by the authed principal — the session UUID alone
  // no longer streams a mediation.
  const res = await request<{ ticket: string }>(
    `/sessions/${encodeURIComponent(sessionId)}/stream-ticket`,
    { method: 'POST', body: '{}' },
    accessToken,
  );
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const url = `${proto}//${window.location.host}/api/mediator/sessions/${encodeURIComponent(
    sessionId,
  )}/transcript/stream?ticket=${encodeURIComponent(res.ticket)}`;
  return new WebSocket(url);
}
